import { useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ActionCardMetadata } from '../../generated/chatActionTypes'
import { createNadDebug } from '../../utils/debugLog'
import { useActionDestinations } from './actionDestinations'

const log = createNadDebug('director-action-card')

/** Optional navigation: rendering history never executes the action. */
export function ActionCard({ metadata, cardId }: { metadata: ActionCardMetadata; cardId: string }) {
  const { t } = useTranslation('chat')
  const { destinations, destinationView } = useActionDestinations(cardId)
  const destination = destinations[metadata.action.destination]
  const inFlight = useRef(false)
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function open() {
    if (inFlight.current) return
    inFlight.current = true
    setOpening(true)
    setError(null)
    const interactionId = crypto.randomUUID()
    const context = { card_id: cardId, interaction_id: interactionId, destination: metadata.action.destination }
    log({ event: 'action-card:clicked', ...context })
    try {
      await destination.open(metadata.action, interactionId)
      log({ event: 'action-card:destination-open-requested', ...context })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('actionCard.failed'))
      log({ event: 'action-card:unavailable', ...context })
    } finally {
      inFlight.current = false
      setOpening(false)
    }
  }

  return (
    <div className="rounded-xl border border-border bg-surface-1 p-4" data-testid="director-action-card" data-card-id={cardId}>
      <p className="text-base font-medium text-foreground">{destination.title}</p>
      {metadata.target_label && <p className="mt-1 break-words text-base text-foreground">{metadata.target_label}</p>}
      <p className="mt-1 text-sm text-muted-foreground">{destination.description}</p>
      <button
        type="button"
        onClick={() => void open()}
        disabled={opening}
        className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50"
      >
        {opening ? t('actionCard.opening') : destination.label}
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </button>
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
      {destinationView}
    </div>
  )
}
